// GA4 adapter. Wraps `gtag` directly (loaded at boot from
// public/index.html) rather than using `react-ga4`. The public API
// in index.js converts vendor-neutral calls into the gtag wire format
// here. No call site outside this file may import gtag/react-ga4 —
// see design §D *Provider abstraction*.

const GTAG_LOADER_ATTR = 'data-lf-gtag';

function ensureGtagLoaded(measurementId) {
    if (typeof window === 'undefined') return;

    // Bootstrap the dataLayer + gtag stub if the host page didn't
    // already set one up. Either path is fine — this runtime stub is
    // identical to the inline one in lf_fe/public/index.html — but we
    // must NOT skip the loader-script injection just because a stub
    // exists, since the stub only queues; the real ingest only happens
    // once gtag.js has loaded.
    window.dataLayer = window.dataLayer || [];
    if (typeof window.gtag !== 'function') {
        window.gtag = function gtag() {
            window.dataLayer.push(arguments);
        };
        window.gtag('js', new Date());
    }

    // Inject the loader exactly once. The check is on the loader
    // <script> tag, not on window.gtag — the host page may set up a
    // queueing stub before us (per design §G *Consent Mode v2 loaded
    // before any gtag library*) and we still need to pull in
    // googletagmanager.com/gtag/js to actually transmit.
    if (!document.querySelector(`script[${GTAG_LOADER_ATTR}]`)) {
        const s = document.createElement('script');
        s.async = true;
        s.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(measurementId)}`;
        s.setAttribute(GTAG_LOADER_ATTR, '1');
        document.head.appendChild(s);
    }
}

function applyConsentDefaults() {
    if (!window.gtag) return;
    // Consent Mode v2 defaults: deny ads + analytics until the user
    // grants. ads_data_redaction strips ad params from URLs even if
    // consent is later granted-then-denied.
    window.gtag('consent', 'default', {
        ad_storage: 'denied',
        ad_user_data: 'denied',
        ad_personalization: 'denied',
        analytics_storage: 'denied',
        ads_data_redaction: true,
    });
}

export function makeGa4Adapter({ measurementId, debug = false, environment = 'prod' } = {}) {
    let configured = false;
    let currentUserId = null;
    let currentUserProps = {};

    function configIfNeeded() {
        if (configured || typeof window === 'undefined' || !window.gtag) return;
        const config = {
            send_page_view: false, // we fire `page_view` ourselves with route metadata
            debug_mode: debug ? true : undefined,
            anonymize_ip: true,
            allow_google_signals: false,
            allow_ad_personalization_signals: false,
        };
        // Strip undefined keys.
        Object.keys(config).forEach((k) => config[k] === undefined && delete config[k]);
        window.gtag('config', measurementId, config);
        // Capture client_id into a first-party cookie so the backend
        // can read it for Measurement Protocol calls.
        try {
            window.gtag('get', measurementId, 'client_id', (cid) => {
                if (!cid) return;
                document.cookie = `lf_ga_cid=${encodeURIComponent(cid)}; path=/; SameSite=Lax; max-age=${60 * 60 * 24 * 365 * 2}`;
            });
        } catch (_e) {
            /* ignore */
        }
        configured = true;
    }

    function applyUserContext(params) {
        const merged = { ...params, environment };
        if (currentUserId) merged.user_id = currentUserId;
        // user_properties are sent via `set` calls; don't duplicate as event params.
        return merged;
    }

    return {
        name: 'ga4',
        init() {
            if (typeof window === 'undefined') return;
            ensureGtagLoaded(measurementId);
            applyConsentDefaults();
            configIfNeeded();
        },
        track(name, params, context) {
            if (typeof window === 'undefined' || !window.gtag) return;
            configIfNeeded();
            const ctxUserId = context && context.userId ? context.userId : currentUserId;
            const finalParams = applyUserContext(params || {});
            if (ctxUserId) finalParams.user_id = ctxUserId;
            window.gtag('event', name, finalParams);
        },
        identify(userId, userProps) {
            currentUserId = userId || null;
            currentUserProps = userProps ? { ...userProps } : {};
            if (typeof window === 'undefined' || !window.gtag) return;
            configIfNeeded();
            // Set user_id on the config so subsequent events carry it.
            window.gtag('config', measurementId, {
                user_id: userId || undefined,
                send_page_view: false,
            });
            if (userProps && Object.keys(userProps).length > 0) {
                window.gtag('set', 'user_properties', userProps);
            }
        },
        setConsent(state) {
            if (typeof window === 'undefined' || !window.gtag) return;
            window.gtag('consent', 'update', {
                // Learnforest never enables advertising storage or signals.
                // Product consent controls analytics storage only.
                ad_storage: 'denied',
                ad_user_data: 'denied',
                ad_personalization: 'denied',
                analytics_storage: state === 'granted' ? 'granted' : 'denied',
            });
        },
        pageView(routeId, params, context) {
            if (typeof window === 'undefined' || !window.gtag) return;
            configIfNeeded();
            const ctxUserId = context && context.userId ? context.userId : currentUserId;
            const finalParams = applyUserContext({ route_id: routeId, ...(params || {}) });
            if (ctxUserId) finalParams.user_id = ctxUserId;
            window.gtag('event', 'page_view', finalParams);
        },
        // Test seam — production code never reads these.
        _state() {
            return { configured, currentUserId, currentUserProps };
        },
    };
}

export default makeGa4Adapter;
